<template>
    <div id="account-status">
        <div>
            <h5 class="mb-3">Status: <span :class="{ flash: shouldHighlightRetirementStatus }">{{ isInRetirementText }}</span></h5>

            <div class="alert alert-info mb-0" role="alert">
                <span v-if="isRetired">
                    You have told us that you have started taking cash or pension from your pension pots or you are over
                    the age where you are entitled to your state pension.
                    Guiide will always calculate your plan from the next month.
                </span>
                <span v-else>
                    You are planning for retirement. You can change your retirement date if you wish to consider
                    different scenarios.
                </span>
            </div>

            <div v-if="isRetired" class="mt-3">
                <p>
                    Use the 'Reset' button if you would like to change your account so Guiide considers you to be pre-retirement.
                    This will let you adjust your retirement date again. Your retirement date will be set to next month, but
                    you can change it to any future date.
                </p>

                <p class="text-danger">
                    {{ resetErrorText }}
                </p>

                <div class="row">
                    <div class="d-md-flex">
                        <button type="button"
                                class="btn me-md-2"
                                :class="buttonAction"
                                aria-label="Reset"
                                :disabled="resetDisabled"
                                @click="handleReset">
                            {{ buttonText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from "vue";
    import { useStore } from "vuex";
    import { app } from "@/guiide/ts/app";

    const store = useStore();

    let resetTimeoutId = null;

    const shouldHighlightRetirementStatus = ref(false);
    const resetClicked = ref(false);
    const resetDisabled = ref(false);
    const resetErrorText = ref('');

    const buttonText = computed(() => resetClicked.value ? "Confirm reset" : "Reset");

    const buttonAction = computed(() => resetClicked.value ? "btn-danger" : "btn-warning");

    const isRetired = computed(() => store.state.retiree?.alreadyDrawingPension ?? false);

    const isInRetirementText = computed(() => isRetired.value ? 'in-retirement' : 'pre-retirement');

    watch(
        () => isRetired.value,
        (newValue: boolean, oldValue: boolean) => {
            if (oldValue && !newValue) {
                shouldHighlightRetirementStatus.value = true;

                setTimeout(() => {
                    shouldHighlightRetirementStatus.value = false;
                }, 2000);
            }
        }
    )

    async function handleReset() {
        if (!resetClicked.value) {
            resetClicked.value = true;
            resetTimeoutId = setTimeout(() => {
                resetClicked.value = false;
                resetTimeoutId = null;
            }, 5000);
        } else {
            if (resetTimeoutId) {
                clearTimeout(resetTimeoutId);
                resetTimeoutId = null;
            }

            resetDisabled.value = true;
            resetErrorText.value = "";

            const resetResult = await app.resetCrossover();

            if (!resetResult.successful) {
                resetErrorText.value = resetResult.message;
                resetClicked.value = false;
                resetDisabled.value = false;
            }
        }
    }
</script>

<style lang="scss">
    @keyframes flash-highlight {
      0%   { background-color: rgba(#e0bd28, 0); }
      20%  { background-color: rgba(#e0bd28, 0.9); }
      60%  { background-color: rgba(#e0bd28, 0.4); }
      100% { background-color: rgba(#e0bd28, 0); }
    }

    .flash {
        display: inline;
        border-radius: 0.2em;
        background-color: transparent;
        animation: flash-highlight 1000ms ease-out 4;
    }
</style>